<template>
  <v-dialog :value="value" max-width="480px" persistent @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>
          Imprimir comprobante
          <span v-if="ticketNumber">#{{ ticketNumber }}</span>
        </span>
        <v-btn icon :disabled="busy" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="errorMessage" dense text type="error" class="mb-3">
          {{ errorMessage }}
        </v-alert>
        <v-alert v-if="statusMessage" dense text type="info" class="mb-3">
          {{ statusMessage }}
        </v-alert>

        <div class="subtitle-2 mb-2">Formato</div>
        <v-radio-group v-model="outputMode" :disabled="busy" row hide-details class="mt-0 mb-4">
          <v-radio value="ticket" label="Ticket" />
          <v-radio value="document" label="Documento" />
        </v-radio-group>

        <div class="subtitle-2 mb-2">Destino de impresión</div>
        <v-radio-group v-model="printMode" :disabled="busy" hide-details class="mt-0 mb-4">
          <v-radio value="browser" label="Navegador (vista de impresión)" />
          <v-radio value="bridge" label="Puente de impresión (Windows)" />
        </v-radio-group>

        <template v-if="printMode === 'bridge'">
          <v-select
            v-model="selectedPrinterKey"
            :items="printerItems"
            :loading="loadingPrinters"
            item-text="label"
            item-value="key"
            label="Impresora"
            outlined
            dense
            hide-details
            class="mb-3"
            no-data-text="No hay puentes en línea con impresoras"
            :disabled="busy"
          />
          <v-select
            v-if="outputMode === 'ticket'"
            v-model="paperWidthMm"
            :items="paperWidthOptions"
            item-text="label"
            item-value="value"
            label="Ancho de papel"
            outlined
            dense
            hide-details
            :disabled="busy"
          />
          <p class="caption grey--text mt-3 mb-0">
            Configure equipos en
            <router-link to="/admin/print-bridges">Puentes de impresión</router-link>.
          </p>
        </template>

        <p v-else class="caption grey--text mb-0">
          Se abrirá la ventana de impresión del navegador con la plantilla
          {{ outputMode === 'document' ? 'tamaño carta' : 'del ticket' }}.
        </p>
      </v-card-text>

      <v-card-actions>
        <v-btn text :disabled="busy" @click="close">Cancelar</v-btn>
        <v-btn
          v-if="outputMode === 'document'"
          text
          color="secondary"
          :loading="sharing"
          :disabled="busy || !receiptId"
          @click="shareDocument"
        >
          <v-icon left small>mdi-share-variant</v-icon>
          Compartir
        </v-btn>
        <v-spacer />
        <v-btn
          color="primary"
          :loading="busy"
          :disabled="!canSubmit"
          @click="submit"
        >
          Imprimir
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  fetchTicketFinalPdf,
  renderTicketFinal,
} from '@/services/posSalesService';
import {
  binaryToBase64,
  createPrintJob,
  htmlToBase64,
  listRemotePrinters,
  waitForPrintJob,
} from '@/services/printBridgeService';
import { shareOrDownloadPdf } from '@/utils/documentOutput';

export default {
  name: 'TicketReprintDialog',
  props: {
    value: { type: Boolean, default: false },
    receiptId: { type: String, default: null },
    ticketNumber: { type: [Number, String], default: null },
  },
  data() {
    return {
      outputMode: 'ticket',
      printMode: 'bridge',
      loadingPrinters: false,
      rendering: false,
      printing: false,
      sharing: false,
      errorMessage: '',
      statusMessage: '',
      remotePrinters: [],
      selectedPrinterKey: null,
      paperWidthMm: 80,
      paperWidthOptions: [
        { value: 58, label: '58 mm' },
        { value: 80, label: '80 mm' },
      ],
      renderedHtml: '',
      renderedOutputMode: null,
      pdfBlob: null,
    };
  },
  computed: {
    busy() {
      return this.loadingPrinters || this.rendering || this.printing || this.sharing;
    },
    businessKey() {
      return localStorage.getItem('businessId') || 'default';
    },
    printerStorageKey() {
      return `posRemotePrinter:${this.businessKey}`;
    },
    paperStorageKey() {
      return `posRemotePaperWidth:${this.businessKey}`;
    },
    printerItems() {
      return (this.remotePrinters || [])
        .filter((printer) => printer.online)
        .map((printer) => ({
          key: `${printer.bridgeId}::${printer.name}`,
          label: `${printer.bridgeName} · ${printer.displayName || printer.name}`,
          bridgeId: printer.bridgeId,
          printerName: printer.name,
        }));
    },
    canSubmit() {
      if (!this.receiptId || this.busy) return false;
      if (this.printMode === 'browser') return true;
      return Boolean(this.selectedPrinterKey);
    },
  },
  watch: {
    value(open) {
      if (open) this.bootstrap();
    },
    outputMode() {
      this.renderedHtml = '';
      this.renderedOutputMode = null;
      this.pdfBlob = null;
      this.errorMessage = '';
      this.statusMessage = '';
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
    },
    async bootstrap() {
      this.errorMessage = '';
      this.statusMessage = '';
      this.renderedHtml = '';
      this.renderedOutputMode = null;
      this.pdfBlob = null;
      this.outputMode = 'ticket';
      this.printMode = 'bridge';
      const savedPaper = Number(localStorage.getItem(this.paperStorageKey));
      this.paperWidthMm = savedPaper === 58 || savedPaper === 80 ? savedPaper : 80;
      await this.loadPrinters();
    },
    async loadPrinters() {
      this.loadingPrinters = true;
      try {
        const data = await listRemotePrinters();
        this.remotePrinters = data.printers || [];
        const items = this.printerItems;
        const saved = localStorage.getItem(this.printerStorageKey);
        if (saved && items.some((item) => item.key === saved)) {
          this.selectedPrinterKey = saved;
        } else if (items.length) {
          this.selectedPrinterKey = items[0].key;
        } else {
          this.selectedPrinterKey = null;
          this.printMode = 'browser';
        }
      } catch (error) {
        this.remotePrinters = [];
        this.selectedPrinterKey = null;
        this.printMode = 'browser';
        this.errorMessage = (error.response && error.response.data && error.response.data.mensaje)
          || 'No se pudieron cargar las impresoras remotas. Puede usar el navegador.';
      } finally {
        this.loadingPrinters = false;
      }
    },
    async ensureHtml() {
      if (this.renderedHtml && this.renderedOutputMode === this.outputMode) {
        return this.renderedHtml;
      }
      this.rendering = true;
      this.statusMessage = 'Preparando plantilla…';
      try {
        const rendered = await renderTicketFinal(this.receiptId, this.outputMode);
        this.renderedHtml = rendered.html || rendered.previewHtml || '';
        this.renderedOutputMode = this.outputMode;
        if (!this.renderedHtml) {
          throw new Error('No hay plantilla disponible para este ticket.');
        }
        return this.renderedHtml;
      } finally {
        this.rendering = false;
      }
    },
    async ensurePdf() {
      if (this.pdfBlob) return this.pdfBlob;
      this.statusMessage = 'Generando PDF tamaño carta…';
      this.pdfBlob = await fetchTicketFinalPdf(this.receiptId);
      return this.pdfBlob;
    },
    printBrowser(html) {
      const windowFeatures = this.outputMode === 'document'
        ? 'width=900,height=720'
        : 'width=420,height=640';
      const printWindow = window.open('', '_blank', windowFeatures);
      if (!printWindow) {
        throw new Error('El navegador bloqueó la ventana de impresión.');
      }
      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    },
    async printBridge(html) {
      const selected = this.printerItems.find((item) => item.key === this.selectedPrinterKey);
      if (!selected) {
        throw new Error('Seleccione una impresora.');
      }
      localStorage.setItem(this.printerStorageKey, selected.key);
      const isDocument = this.outputMode === 'document';
      const paperWidthMm = this.paperWidthMm === 58 ? 58 : 80;
      if (!isDocument) localStorage.setItem(this.paperStorageKey, String(paperWidthMm));
      const pdf = isDocument ? await this.ensurePdf() : null;

      this.statusMessage = 'Enviando a la impresora…';
      const created = await createPrintJob({
        bridgeId: selected.bridgeId,
        printerName: selected.printerName,
        format: isDocument ? 'pdf' : 'html',
        dataBase64: isDocument ? await binaryToBase64(pdf) : htmlToBase64(html),
        documentName: `${isDocument ? 'Comprobante' : 'Ticket'}-${this.ticketNumber || 'reprint'}`,
        ...(!isDocument ? { paperWidthMm } : {}),
      });
      this.statusMessage = 'Esperando confirmación del puente…';
      const job = await waitForPrintJob(created.job.jobId);
      if (job.status === 'completed') {
        return;
      }
      if (job.status === 'accepted' || job.status === 'sent' || job.status === 'queued') {
        throw new Error('La impresora recibió el trabajo, pero el puente no confirmó a tiempo.');
      }
      throw new Error(job.resultMessage || `Impresión ${job.status}`);
    },
    async shareDocument() {
      if (!this.receiptId || this.busy) return;
      this.sharing = true;
      this.errorMessage = '';
      this.statusMessage = '';
      try {
        const pdf = await this.ensurePdf();
        const result = await shareOrDownloadPdf({
          blob: pdf,
          filename: `Comprobante-${this.ticketNumber || 'venta'}.pdf`,
          title: `Comprobante de venta ${this.ticketNumber ? `#${this.ticketNumber}` : ''}`.trim(),
        });
        if (result === 'shared') this.statusMessage = 'Documento compartido.';
        if (result === 'downloaded') this.statusMessage = 'El PDF se descargó para compartirlo.';
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.mensaje)
          || error.message
          || 'No se pudo compartir el documento.';
      } finally {
        this.sharing = false;
      }
    },
    async submit() {
      if (!this.canSubmit) return;
      this.printing = true;
      this.errorMessage = '';
      this.statusMessage = '';
      try {
        const html = await this.ensureHtml();
        if (this.printMode === 'browser') {
          this.printBrowser(html);
          this.$emit('printed', {
            mode: 'browser',
            outputMode: this.outputMode,
            receiptId: this.receiptId,
          });
          this.close();
          return;
        }
        await this.printBridge(html);
        this.$emit('printed', {
          mode: 'bridge',
          outputMode: this.outputMode,
          receiptId: this.receiptId,
        });
        this.close();
      } catch (error) {
        this.errorMessage = (error.response && error.response.data && error.response.data.mensaje)
          || error.message
          || 'No se pudo imprimir el comprobante.';
        this.statusMessage = '';
      } finally {
        this.printing = false;
      }
    },
  },
};
</script>
